import React from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@components/MetricCard';
import { CallMetrics } from '../types';
import { useAuth } from '../../Auth';

interface CallMetricsProps {
  metrics: CallMetrics | null;
}

export const CallMetricsCards: React.FC<CallMetricsProps> = ({ metrics }) => {
  const { t } = useTranslation();
  const { organization } = useAuth();
  const isInvoiceBilled = Boolean(organization?.is_invoice_billed);

  if (!metrics) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[1, 2, 3, 4].map(i => (
          <MetricCard key={i} title={t('common.loading')} value="" loading={true} />
        ))}
      </div>
    );
  }

  const metricCards = [
    {
      title: t('calls.activeCalls'),
      value: metrics.active_calls_count,
      icon: 'phone',
      variant: 'primary' as const,
      trend: t('calls.currentlyInProgress'),
      trendType: 'neutral' as const,
    },
    {
      title: t('calls.totalCallsToday'),
      value: metrics.today_calls_count,
      icon: 'call',
      variant: 'success' as const,
      trend: t('calls.callsCompletedToday'),
      trendType: 'positive' as const,
    },
    {
      title: t('calls.avgCallDuration'),
      value: `${Math.floor(metrics.avg_call_duration / 60)}:${Math.floor(
        metrics.avg_call_duration % 60
      )
        .toString()
        .padStart(2, '0')}`,
      icon: 'schedule',
      variant: 'info' as const,
      trend: t('calls.averageCallLength'),
      trendType: 'neutral' as const,
    },
    {
      title: t('calls.walletBalance'),
      value: `${metrics.wallet_balance.toFixed(2)} €`,
      icon: 'account_balance_wallet',
      variant: 'warning' as const,
      trend:
        metrics.wallet_balance > 0 ? t('calls.balanceAvailable') : t('calls.insufficientBalance'),
      trendType: metrics.wallet_balance > 0 ? ('positive' as const) : ('negative' as const),
    },
  ];

  const visibleCards = isInvoiceBilled
    ? metricCards.filter(c => c.icon !== 'account_balance_wallet')
    : metricCards;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {visibleCards.map((card, index) => (
        <MetricCard
          key={index}
          title={card.title}
          value={card.value}
          icon={card.icon}
          variant={card.variant}
          trend={card.trend}
          trendType={card.trendType}
        />
      ))}
    </div>
  );
};
